/**
 * PrivacyPromisePage — the canonical SonSpeak privacy promise.
 *
 * Word-for-word from the founder + legal advisory (2026-05). This is the
 * sentence we build the product around. Linked from the public footer,
 * the Manage Board privacy banner, and the Notifications page header.
 */
import { Link } from "react-router-dom";
import { Shield, Lock, EyeOff, Smartphone, Sparkles } from "lucide-react";
import { useSEO } from "@/lib/seo";

export default function PrivacyPromisePage() {
  useSEO({
    title: "Our privacy promise — SonSpeak",
    description: "Children should be able to use AAC without becoming cloud data.",
  });
  return (
    <div className="page-wrap pt-6 pb-24" data-testid="privacy-promise-page">
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="display text-3xl sm:text-4xl font-extrabold mb-4 flex items-center gap-2">
          <Shield size={28} aria-hidden /> Our privacy promise
        </h1>

        <div className="brutal-card bg-[color:var(--cream)] p-5 mb-6">
          <p className="text-base sm:text-lg leading-relaxed" data-testid="privacy-promise-caregiver-frame">
            SonSpeak is built for <strong>parents and adult caregivers</strong>. Parents create and manage everything; children don&apos;t have accounts, make purchases, or manage settings. The child is the beneficiary — the parent is always in control.
          </p>
        </div>

        <div className="brutal-card bg-[color:var(--cream)] p-5 mb-6">
          <p className="text-base sm:text-lg leading-relaxed">
            SonSpeak is designed so children can use AAC supports without
            becoming cloud data. <strong>Child profiles, photos, voice
            recordings, button usage, and developmental settings stay local
            to the parent, school, or therapist's device</strong> unless an
            adult intentionally exports them. SonSpeak only collects limited
            adult account and billing information needed to operate the
            service.
          </p>
        </div>

        <Section icon={EyeOff} title="What we never see">
          <ul className="list-disc list-inside space-y-1">
            <li>Your child's full legal name, date of birth, address</li>
            <li>Diagnosis, medical records, therapy notes</li>
            <li>Voice recordings or photos on a tile</li>
            <li>Which buttons your child presses or how often</li>
            <li>School records, IEPs, classroom assignments</li>
            <li>Any disability detail you don't intentionally export</li>
          </ul>
        </Section>

        <Section icon={Smartphone} title="What stays on your device">
          <p className="mb-2">For each parent, therapist, or school using
          the app, this data lives on <strong>their own device only</strong>:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Child's nickname (we recommend "Buddy", "Sam", "Student A")</li>
            <li>Communication buttons, labels, categories, colors</li>
            <li>Photos and voice recordings on every button</li>
            <li>Button-press history and usage patterns</li>
            <li>Age and developmental settings</li>
            <li>Routines and AAC preferences</li>
          </ul>
        </Section>

        <Section icon={Lock} title="What we do collect (adult only)">
          <p className="mb-2">Only what&apos;s necessary to run the service:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Your email and adult account name</li>
            <li>Billing status and subscription plan</li>
            <li>Consent records (the timestamps when you grant or revoke consent)</li>
            <li>Support ticket history</li>
            <li>School or therapist organization name (for our B2B accounts)</li>
            <li>Login and security metadata</li>
            <li><strong>An encrypted envelope of any child details you choose to save</strong> — sealed with your password before upload. We cannot decrypt it, ever.</li>
          </ul>
        </Section>

        <Section icon={Sparkles} title="The AI Coach">
          <p>
            The AI Coach never receives your child's identifying information.
            Please don't enter your child's full name, date of birth,
            diagnosis, school, medical info, or therapy notes — the coach
            gives better, more general advice without them. Try
            <em> "my child uses AAC and needs help with morning routine
            buttons"</em> rather than
            <em> "my son John Smith, age 6, diagnosed with autism at ABC
            Elementary..."</em>
          </p>
        </Section>

        <Section icon={Shield} title="The voice + photo rule">
          <p className="text-base font-bold mb-2">
            SonSpeak does not collect, transmit, view, or store children's
            voice recordings or photos on SonSpeak servers.
          </p>
          <p>
            Voice and photos live in your device&apos;s local storage. They
            never travel through our servers. Even if our cloud database
            were stolen tomorrow, attackers would find zero children&apos;s
            faces and zero children&apos;s voices.
          </p>
        </Section>

        <Section icon={Smartphone} title="If you lose your device">
          <p>
            Because child data is local, losing your device means losing
            local data. We&apos;re building an <strong>encrypted backup &amp; export tool</strong>
            — protected by your password — so you can move your
            child&apos;s library to a new device without putting their data on
            our servers. Your password is the only key. We never see it.
          </p>
          <p className="mt-2 text-sm text-[color:var(--ink-soft)]">
            <em>Coming soon — file-based export is in active development.</em>
          </p>
        </Section>

        <Section icon={Lock} title="Schools &amp; therapists">
          <p>
            The school or therapist keeps each child&apos;s records locally on
            their device. SonSpeak provides the tool — we are not the
            custodian of student or patient records, an architecture
            designed to keep schools and therapists out of the data-custody
            business.
          </p>
        </Section>

        <div className="brutal-card bg-white p-4 mt-6 text-sm text-[color:var(--ink-soft)]">
          Questions? Email <a href="mailto:support@sonspeak.com" className="underline font-semibold">support@sonspeak.com</a>.
          We read every message.
        </div>

        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/security" className="underline" data-testid="privacy-promise-back-security">Security details</Link>
          <Link to="/data-protection" className="underline" data-testid="privacy-promise-back-dp">Data protection</Link>
          <Link to="/" className="underline" data-testid="privacy-promise-back-home">Home</Link>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="brutal-card bg-white p-5 mb-4">
      <h2 className="display text-lg font-extrabold mb-2 flex items-center gap-2">
        <Icon size={18} aria-hidden /> {title}
      </h2>
      <div className="text-sm leading-relaxed text-[color:var(--ink)]/85">
        {children}
      </div>
    </section>
  );
}
